/**
 * Global confirm modal — XSS-safe, accessible, returns Promise<boolean>.
 *
 * @param {{ title?: string, message?: string, confirmLabel?: string, cancelLabel?: string, danger?: boolean }} options
 * @returns {Promise<boolean>} true = confirmed, false = cancelled
 */
export function confirmModal({
  title = 'Confirm',
  message = 'Are you sure?',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger = false
} = {}) {
  return new Promise((resolve) => {
    let settled = false;
    const cleanup = (result) => {
      if (settled) return;
      settled = true;
      document.removeEventListener('keydown', onKey);
      overlay.remove();
      resolve(result);
    };

    // Overlay
    const overlay = document.createElement('div');
    overlay.className = 'g-modal-overlay';
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) cleanup(false);
    });

    // Dialog
    const dialog = document.createElement('div');
    dialog.className = 'g-modal';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-labelledby', 'g-modal-title');
    dialog.setAttribute('aria-describedby', 'g-modal-desc');

    // Title
    const titleEl = document.createElement('h3');
    titleEl.id = 'g-modal-title';
    titleEl.className = 'g-modal__title';
    titleEl.textContent = String(title);

    // Message
    const msgEl = document.createElement('p');
    msgEl.id = 'g-modal-desc';
    msgEl.className = 'g-modal__msg';
    msgEl.textContent = String(message);

    // Footer
    const footer = document.createElement('div');
    footer.className = 'g-modal__footer';

    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'g-modal__btn g-modal__btn--cancel';
    cancelBtn.textContent = String(cancelLabel);
    cancelBtn.onclick = () => cleanup(false);

    const confirmBtn = document.createElement('button');
    confirmBtn.type = 'button';
    confirmBtn.className = `g-modal__btn g-modal__btn--confirm${danger ? ' g-modal__btn--danger' : ''}`;
    confirmBtn.textContent = String(confirmLabel);
    confirmBtn.onclick = () => cleanup(true);

    footer.appendChild(cancelBtn);
    footer.appendChild(confirmBtn);

    dialog.appendChild(titleEl);
    dialog.appendChild(msgEl);
    dialog.appendChild(footer);
    overlay.appendChild(dialog);

    const onKey = (e) => {
      if (e.key === 'Escape') cleanup(false);
    };
    document.addEventListener('keydown', onKey);
    document.body.appendChild(overlay);

    // Focus cancel by default (safer — avoids accidental confirm on Enter)
    requestAnimationFrame(() => cancelBtn.focus());
  });
}
